import React, { useEffect, useState } from "react";
import { useParams, Link, Navigate } from "react-router-dom";
import axios from "axios";
import SEO from "@/components/SEO";
import PackageCard from "@/components/PackageCard";
import { USTADZ_BY_SLUG, USTADZ } from "@/data/ustadz";
import {
  ArrowLeft, Phone, GraduationCap, ChevronRight, Sparkles, Package as PackageIcon,
  MessageCircle, MapPin, Calendar,
} from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL;
const HERO_BG = `${API}/api/static/hero/about.jpg`;

const api = axios.create({ baseURL: `${API}/api` });

export default function UstadzDetail() {
  const { slug } = useParams();
  const ustadz = USTADZ_BY_SLUG[slug];
  const [packages, setPackages] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!ustadz) return;
    api.get(`/ustadz/${slug}/packages`)
      .then((r) => setPackages(r.data || []))
      .catch(() => setPackages([]))
      .finally(() => setLoading(false));
  }, [slug, ustadz]);

  if (!ustadz) return <Navigate to="/" replace />;

  const waMsg = `Assalamualaikum, saya ingin konsultasi program umrah / haji yang dibimbing oleh ${ustadz.name}.`;
  const waUrl = `https://wa.me/6282114294101?text=${encodeURIComponent(waMsg)}`;

  return (
    <div className="bg-background" data-testid="ustadz-detail-page">
      <SEO
        title={`${ustadz.name} — Pembimbing Umrah Batik Travel`}
        titleEn={`${ustadz.name} — Umrah Guide at Batik Travel`}
        description={`${ustadz.name} ${ustadz.title ? `(${ustadz.title})` : ""} — ${ustadz.bio}`}
      />

      {/* HERO */}
      <section className="relative pt-32 lg:pt-40 pb-20 overflow-hidden isolate">
        <div className="absolute inset-0 bg-cover bg-center" style={{ backgroundImage: `url(${HERO_BG})` }} aria-hidden />
        <div className="absolute inset-0 bg-gradient-to-br from-primary/95 via-primary/90 to-primary/70" aria-hidden />
        <div className="pointer-events-none absolute bottom-0 inset-x-0 h-32 bg-gradient-to-b from-transparent to-background" aria-hidden />
        <div className="container-x relative">
          <Link to="/#ustadz" className="inline-flex items-center gap-1.5 text-white/80 hover:text-accent text-sm mb-8" data-testid="back-to-home">
            <ArrowLeft className="h-4 w-4" /> Semua Ustadz
          </Link>
          <div className="grid lg:grid-cols-12 gap-10 items-center">
            <div className="lg:col-span-4">
              <div className="aspect-square rounded-2xl overflow-hidden border border-accent/40 shadow-2xl bg-slate-100 max-w-sm">
                <img src={ustadz.img} alt={ustadz.name} className="w-full h-full object-cover" />
              </div>
            </div>
            <div className="lg:col-span-8">
              <p className="text-accent font-medium text-xs tracking-[0.25em] uppercase mb-3">Pembimbing Batik Travel</p>
              <h1 className="font-serif text-4xl lg:text-6xl text-white leading-[1.05] tracking-tight" data-testid="ustadz-name">
                {ustadz.name}
              </h1>
              {ustadz.title && <p className="mt-3 font-serif italic text-accent text-xl">{ustadz.title}</p>}
              <p className="mt-6 text-lg text-white/85 leading-relaxed max-w-2xl">{ustadz.bio}</p>

              {ustadz.specialties?.length > 0 && (
                <div className="mt-6 flex flex-wrap gap-2">
                  {ustadz.specialties.map((s, i) => (
                    <span key={i} className="text-xs bg-white/10 backdrop-blur border border-white/20 text-white px-3 py-1.5 rounded-full" data-testid={`specialty-${i}`}>
                      {s}
                    </span>
                  ))}
                </div>
              )}

              <div className="mt-9 flex flex-wrap gap-3">
                <a
                  href={waUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid="wa-cta"
                  className="inline-flex items-center gap-2 bg-accent text-primary font-semibold px-7 py-3.5 rounded-full hover:bg-accent/90 transition-colors shadow-lg"
                >
                  <Phone className="h-4 w-4" /> Konsultasi Program
                </a>
                <Link
                  to="/packages"
                  data-testid="all-packages"
                  className="inline-flex items-center gap-2 border border-white/40 text-white px-7 py-3.5 rounded-full hover:bg-white/10 transition-colors"
                >
                  Lihat Semua Paket
                  <ChevronRight className="h-4 w-4" />
                </Link>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* PACKAGES LED BY USTADZ */}
      <section className="py-20 lg:py-28">
        <div className="container-x">
          <div className="flex flex-col lg:flex-row lg:items-end justify-between gap-4 mb-12">
            <div>
              <p className="overline mb-4">Program Bersama {ustadz.name.replace("Ustadz ", "").split(" ")[0]}</p>
              <h2 className="font-serif text-4xl lg:text-5xl text-primary tracking-tight">Paket yang Dibimbing</h2>
              <p className="mt-4 text-muted-foreground max-w-xl leading-relaxed">
                Paket umrah & haji yang akan didampingi langsung oleh {ustadz.name}.
              </p>
            </div>
          </div>

          {loading ? (
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6 animate-pulse">
              {[1, 2, 3].map((i) => (
                <div key={i} className="aspect-[2/3] rounded-lg bg-muted" />
              ))}
            </div>
          ) : packages.length === 0 ? (
            <div className="border border-dashed border-border rounded-lg p-12 text-center" data-testid="no-packages">
              <PackageIcon className="h-10 w-10 text-muted-foreground/40 mx-auto mb-4" />
              <p className="text-muted-foreground">
                Saat ini belum ada jadwal paket yang dibimbing oleh {ustadz.name}.
              </p>
              <Link to="/packages" className="mt-4 inline-flex items-center gap-1.5 text-accent hover:underline text-sm">
                Lihat semua paket <ChevronRight className="h-4 w-4" />
              </Link>
            </div>
          ) : (
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6" data-testid="ustadz-packages">
              {packages.map((p) => <PackageCard key={p.id} pkg={p} />)}
            </div>
          )}
        </div>
      </section>

      {/* OTHER USTADZ */}
      <section className="py-20 lg:py-24 bg-muted/40 border-y border-border">
        <div className="container-x">
          <p className="overline mb-4 text-center">Pembimbing Lainnya</p>
          <h2 className="font-serif text-3xl lg:text-4xl text-primary tracking-tight text-center">Ustadz Batik Travel</h2>
          <div className="mt-10 grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-4">
            {USTADZ.filter((u) => u.slug !== slug).slice(0, 12).map((u) => (
              <Link
                key={u.slug}
                to={`/ustadz/${u.slug}`}
                data-testid={`other-ustadz-${u.slug}`}
                className="group text-center"
              >
                <div className="aspect-square rounded-lg overflow-hidden bg-slate-100 ring-1 ring-border group-hover:ring-2 group-hover:ring-accent transition-all">
                  <img src={u.img} alt={u.name} className="w-full h-full object-cover" loading="lazy" />
                </div>
                <p className="mt-3 text-xs font-medium text-primary line-clamp-2 group-hover:text-accent transition-colors">{u.name}</p>
              </Link>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
}
